/**
 * E2 -- the executive surfaces have a way in.
 *
 * The board snapshot, provable assurance, resilience readiness and the
 * sector benchmark were all routed and none of them appeared in any
 * navigation. The only way to reach the board -- the surface a CRO
 * opens first -- was to type the URL or find a tile somebody had
 * happened to place. M4 J3 step 1 was "type /board".
 *
 * Each tile is shown only to somebody who may actually open it. E1
 * just removed a strip that offered nine rooms which would all refuse
 * the viewer; adding four more here would be the same mistake.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { api } from "shared/api/client";
import { useTranslation } from "react-i18next";

import { ExecutiveBrief } from "shared/executive/ExecutiveBrief";
import { canReadBoard, hasModule } from "shared/api/authority";
import { hasPosition, useMe } from "shared/api/useMe";
import { ModuleRubric, Tile, TileSection } from "shared/layout/ModuleHomeTile";

/** Who may see anything here at all -- drives the nav entry too. */
export function canSeeExecutive(
  me: { is_admin?: boolean; positions?: string[]; realm_roles?: string[] } | undefined,
): boolean {
  if (!me) return false;
  if (me.is_admin) return true;
  const positions = me.positions ?? [];
  if (positions.includes("approver") || positions.includes("reviewer")) return true;
  return (me.realm_roles ?? []).includes("head_of_opsres");
}

export function ExecutiveHome() {
  const { t } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.is_admin === true;
  // The board, assurance and the benchmark are approver-gated on the
  // server (admin admitted). Readiness admits a reviewer and the
  // head of ops res as well.
  const approverish = isAdmin || hasPosition(me.data, "approver");
  // A7: the board READS for the reviewer now. His home quotes its
  // headline at him, so hiding the page it came from told him the
  // number was not his business while showing it to him. Issuing the
  // signed PDF is still the approver's act.
  // Round 14 B4 -- the board tile reads the same rule as the route
  // (`canReadBoard` mirrors the server's guard), so the Head of
  // Operational Resilience, whom the route admits, is offered it.
  const boardish = canReadBoard(me.data);
  // The field-exam room keeps its own seats: approver or reviewer.
  const fieldExamish = approverish || hasPosition(me.data, "reviewer");
  const readinessish =
    boardish || (me.data?.realm_roles ?? []).includes("head_of_opsres");

  // Round 10 A3: a seat that can open none of it is not offered the
  // section. The nav entry already hides for those seats; typing the
  // URL used to render the promise, the word REPORTING and then zero
  // links -- a room with the lights on and nothing in it.
  /* The flagged count on the tile. A failure leaves the tile with its
     plain description rather than removing the room: the page is
     reachable whether or not this read succeeds. */
  const checks = useQuery({
    queryKey: ["twin", "supervisory-checks", "count"],
    queryFn: () =>
      api.get<{
        checks_total: number;
        checks_flagged: number;
        checks_not_assessed?: number;
      }>("/v1/twin/supervisory-checks"),
    enabled: fieldExamish,
    retry: false,
  });

  const rooms = [boardish, approverish, readinessish, approverish, fieldExamish];
  const openable = rooms.filter(Boolean).length;
  if (openable === 0) {
    return (
      <article data-testid="executive-home" className="space-y-3">
        <header>
          <ModuleRubric>{t("executive.rubric")}</ModuleRubric>
          <h1 className="font-display text-2xl text-slate-900">
            {t("executive.heading")}
          </h1>
        </header>
        {/* Round 18 Q3 -- the owner and the treasurer landed here from
            "What can we prove?" and read that none of it was theirs,
            while the test runs, drills and incident reports under the
            same question were. Those are offered; the rest is said. */}
        {hasModule(me.data, "twin") ? (
          <>
            <p
              data-testid="executive-not-yours"
              className="max-w-2xl text-sm text-slate-700"
            >
              {t("executive.notYoursButThese")}
            </p>
            <ul data-testid="executive-yours-instead" className="space-y-1 text-sm">
              {(
                [
                  ["/twin/test-runs", "nav.tab.twin__test-runs"],
                  ["/twin/drills", "nav.tab.twin__drills"],
                  ["/twin/incident-reports", "nav.tab.twin__incident-reports"],
                ] as const
              ).map(([to, key]) => (
                <li key={to}>
                  <Link to={to} className="text-brand-brassDark hover:underline">
                    {t(key)}
                  </Link>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p
            data-testid="executive-not-yours"
            className="max-w-2xl text-sm text-slate-700"
          >
            {t("executive.notYours")}
          </p>
        )}
        <Link
          to="/my-work"
          data-testid="executive-to-my-work"
          className="text-sm text-brand-brassDark underline decoration-dotted underline-offset-2"
        >
          {t("executive.toMyWork")}
        </Link>
      </article>
    );
  }

  return (
    <article data-testid="executive-home" className="space-y-6">
      <header>
        <ModuleRubric>{t("executive.rubric")}</ModuleRubric>
        <h1 className="font-display text-2xl text-slate-900">
          {t("executive.heading")}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">{t("executive.body")}</p>
        {/* A seat that can open SOME of it is told which. The Head of
            Operational Resilience reads one card of four under a
            standfirst promising the board's, the regulator's and the
            sector's view; without this line the other three are simply
            missing rather than somebody else's. */}
        {openable < rooms.length && (
          <p
            data-testid="executive-partial"
            className="mt-1 max-w-3xl text-xs text-slate-500"
          >
            {t("executive.partial", { open: openable, total: rooms.length })}
          </p>
        )}
      </header>

      <ExecutiveBrief enabled={openable > 0} />

      <TileSection heading={t("executive.section")} testid="executive-tiles">
        <Tile
          to="/board"
          name={t("executive.tiles.board.name")}
          desc={t("executive.tiles.board.desc")}
          who={t("executive.tiles.board.who")}
          testid="executive-tile-board"
          icon="dossier"
          visible={boardish}
        />
        <Tile
          to="/assurance"
          name={t("executive.tiles.assurance.name")}
          desc={t("executive.tiles.assurance.desc")}
          who={t("executive.tiles.assurance.who")}
          testid="executive-tile-assurance"
          icon="verifier"
          visible={approverish}
        />
        <Tile
          to="/readiness"
          name={t("executive.tiles.readiness.name")}
          desc={t("executive.tiles.readiness.desc")}
          who={t("executive.tiles.readiness.who")}
          testid="executive-tile-readiness"
          icon="benchmark"
          visible={readinessish}
        />
        {/* Round 11 C4 -- the room the executives were never offered.
            "20 of 28 supervisory checks flagged" is a number a CRO is
            asked about in a board meeting, and it appeared on none of
            her screens: /twin/field-exam was typed-only and
            /admin/mock-exam refuses her. The count rides the tile, so
            it is on the two-minute read rather than one click past
            it. */}
        <Tile
          to="/twin/field-exam"
          name={t("executive.tiles.fieldExam.name")}
          desc={
            checks.data
              ? /* Round 17 P3 -- "23 of 37 flagged" beside the
                   examiner's "23 / 34; 3 not assessed": a check that
                   could not be assessed is in neither the numerator nor
                   the denominator, and the line says how many. */
                (checks.data.checks_not_assessed ?? 0) > 0
                ? t("executive.tiles.fieldExam.descCountNotAssessed", {
                    flagged: checks.data.checks_flagged,
                    total:
                      checks.data.checks_total - (checks.data.checks_not_assessed ?? 0),
                    notAssessed: checks.data.checks_not_assessed,
                  })
                : t("executive.tiles.fieldExam.descCount", {
                    flagged: checks.data.checks_flagged,
                    total: checks.data.checks_total,
                  })
              : t("executive.tiles.fieldExam.desc")
          }
          who={t("executive.tiles.fieldExam.who")}
          testid="executive-tile-field-exam"
          icon="obligation"
          visible={fieldExamish}
        />
        <Tile
          to="/benchmark"
          name={t("executive.tiles.benchmark.name")}
          desc={t("executive.tiles.benchmark.desc")}
          who={t("executive.tiles.benchmark.who")}
          testid="executive-tile-benchmark"
          icon="benchmark"
          visible={approverish}
        />
        {/* Stitching review -- the build-out ladder retired to Admin as
            "Set up"; this is the one link that keeps it in reach of the
            CRO, who reads it well. */}
        <Tile
          to="/admin/setup"
          name={t("executive.tiles.setup.name")}
          desc={t("executive.tiles.setup.desc")}
          who={t("executive.tiles.setup.who")}
          testid="executive-tile-setup"
          icon="journey"
        />
      </TileSection>
    </article>
  );
}
